import { Route } from '@angular/router';
import { ChangelogComponent } from '@project/changelog/changelog.component';
import { initialDataResolver } from 'app/app.resolvers';
import { LayoutComponent } from 'app/layout/layout.component';
import { anonymousGuard } from './core/auth/guards/anonymous.guard';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { permissionGuard } from './core/auth/guards/permission.guard';
import { Permissions } from './core/auth/permissions.constants';

// @formatter:off

export const appRoutes: Route[] = [
    // Redirect empty path to supply-chain dashboard
    { path: '', pathMatch: 'full', redirectTo: '/supply-chain/dashboard' },

    // Redirect signed-in user to supply-chain dashboard
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: '/supply-chain/dashboard' },

    // Public QR scan route (no authentication required)
    {
        path: 'scan',
        loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
        children: [
            {
                path: ':qrCodeId',
                loadComponent: () =>
                    import('./modules/admin/supply-chain/qr-code/qr-scanner/qr-scanner.component').then((m) => m.QrScannerComponent),
            },
        ],
    },

    // Public QR scanner page (anonymous access only - authenticated users fall through to the main routes)
    {
        path: 'supply-chain/qr-code/scanner',
        canMatch: [anonymousGuard],
        loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./modules/admin/supply-chain/qr-code/qr-scanner/qr-scanner.component').then((m) => m.QrScannerComponent),
            },
        ],
    },

    // Public QR scan route with type+id query params (anonymous access only)
    {
        path: 'supply-chain/qr-code/scan',
        canMatch: [anonymousGuard],
        loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./modules/admin/supply-chain/qr-code/qr-scanner/qr-scanner.component').then((m) => m.QrScannerComponent),
            },
        ],
    },

    // Main authenticated routes
    {
        path: '',
        data: {
            breadcrumb: false,
        },
        canActivate: [AuthGuard],
        canActivateChild: [],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'supply-chain',
                data: {
                    breadcrumb: 'nav.supply_chain.title',
                },
                loadChildren: () => import('./modules/admin/supply-chain/supply-chain.routes').then((m) => m.supplyChainRoutes),
            },
            {
                path: 'documentation',
                canActivate: [permissionGuard],
                data: {
                    breadcrumb: 'nav.documentation',
                    permissions: [Permissions.DocumentationView],
                },
                loadChildren: () => import('./modules/admin/documentation/documentation.routes').then((m) => m.documentationRoutes),
            },
            {
                path: 'changelog',
                data: {
                    breadcrumb: 'nav.changelog',
                    headerText: 'nav.changelog',
                    subHeaderText: 'nav.changelog-description',
                },
                children: [
                    {
                        path: '',
                        component: ChangelogComponent,
                    },
                ],
            },
            // Admin routes
            {
                path: 'admin',
                canActivate: [permissionGuard],
                data: {
                    breadcrumb: 'nav.admin.main',
                    permissions: [Permissions.UserManagementViewList, Permissions.AlertSettingsManage],
                },
                children: [
                    {
                        path: '',
                        redirectTo: '/admin/configurations',
                        pathMatch: 'full',
                    },
                    {
                        path: 'configurations',
                        data: {
                            headerText: 'nav.admin.configurations',
                            breadcrumb: 'nav.admin.configurations',
                            subHeaderText: 'nav.admin.configurations-description',
                        },
                        loadComponent: () =>
                            import('./modules/admin/configurations/configurations.component').then((m) => m.ConfigurationsComponent),
                    },
                    {
                        path: 'users-list',
                        data: {
                            headerText: 'nav.admin.users-list',
                            breadcrumb: 'nav.admin.users-list',
                            subHeaderText: 'nav.admin.users-list-description',
                        },
                        loadComponent: () => import('./modules/admin/users-list/users-list.component').then((m) => m.UsersListComponent),
                    },
                    {
                        path: 'permissions',
                        data: {
                            headerText: 'nav.admin.permissions',
                            breadcrumb: 'nav.admin.permissions',
                            subHeaderText: 'nav.admin.permissions-description',
                        },
                        loadComponent: () =>
                            import('./modules/admin/permissions-list/permissions-list.component').then((m) => m.PermissionsListComponent),
                    },
                    {
                        path: 'logs',
                        canActivate: [permissionGuard],
                        data: {
                            headerText: 'nav.admin.logs',
                            breadcrumb: 'nav.admin.logs',
                            subHeaderText: 'nav.admin.logs_description',
                            permissions: [Permissions.ApplicationLogsView],
                        },
                        loadComponent: () =>
                            import('./modules/admin/supply-chain/logs/application-logs.component').then((m) => m.ApplicationLogsComponent),
                    },
                ],
            },
        ],
    },

    // 401 Unauthorized with Empty Layout
    {
        path: 'unauthorized',
        loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
        children: [
            {
                path: '',
                loadComponent: () => import('./modules/error/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
            },
        ],
    },

    // 404 Not Found with Empty Layout - must be last route (wildcard)
    {
        path: '',
        loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
        children: [
            {
                path: '**',
                loadComponent: () => import('./modules/error/not-found/not-found.component').then((m) => m.NotFoundComponent),
            },
        ],
    },
];
